/**
 * Plugin-agnostic memory-write guard.
 *
 * A single call that replaces the plugin's usual memory-write branch:
 *   classify → scan (Layer 1 regex + Layer 2 LLM) → gate on verdict →
 *   persist to chain (fire-and-forget when allowed) → return decision.
 *
 * Plugins call this from their `before_tool_call` hook. When it returns
 * `{ handled: false }` the call wasn't a memory write and the plugin
 * should fall through to its regular tool-call audit. When
 * `{ handled: true }` the plugin returns `decision` directly.
 */
import type { AgentAuth, MemoryEntry, MemoryScanResult } from "../types.js";
import {
  classifyMemoryWrite,
  type ClassifyMemoryWriteOptions,
} from "./classifier.js";
import { commitMemoryVersion } from "./chain.js";
import { scanMemory } from "./scan.js";

/**
 * Minimal logger accepted by `guardMemoryWrite`. Plugins pass their
 * host runtime's logger (openclaw's `api.logger`, MCP's console, etc.).
 * Only `info` and `warn` are used — omitted methods no-op.
 */
export interface GuardLogger {
  info?: (message: string, meta?: unknown) => void;
  warn?: (message: string, meta?: unknown) => void;
}

export interface GuardMemoryWriteInput extends ClassifyMemoryWriteOptions {
  /** The plugin's raw `before_tool_call` event. */
  event: unknown;
  /** The plugin's `before_tool_call` context. */
  ctx: unknown;
  /** Agent auth used to sign the scan's on-chain audit and the memory-commit tx. */
  auth: AgentAuth;
  /** Judge endpoint override (dev vs prod). */
  endpoint?: string;
  /** Self-hosted judge response-signing pubkey. */
  verifyPubKey?: string;
  /** Org name for chain resolution (public vs org-private chain). */
  orgName?: string;
  /** LLM confidence threshold for yellow escalation. Default 0.6. */
  threshold?: number;
  /** When false, red verdicts log but don't block. Default true. */
  enforce?: boolean;
  /** When true, emit a classifier-probe log line via `logger.info`. */
  debug?: boolean;
  /** Optional logger for debug probe + persist-failure warnings. */
  logger?: GuardLogger;
}

/** Plugin-facing decision. Shape mirrors what plugins return from `before_tool_call`. */
export interface GuardMemoryDecision {
  allow: boolean;
  block?: boolean;
  reason?: string;
}

export interface GuardMemoryWriteResult {
  /** True when this call was a memory write and `decision` was set. False = plugin should run its regular audit. */
  handled: boolean;
  /** Present when handled. Plugin returns this from its hook. */
  decision?: GuardMemoryDecision;
  /** Present when handled and scan ran. Absent when scan threw. */
  scanResult?: MemoryScanResult;
  /** Present when handled. True when a chain-commit was dispatched (fire-and-forget). */
  committed?: boolean;
}

/**
 * Log the classifier-probe surface — what the plugin actually sent and
 * whether the SDK's classifier picked it up. Useful when a plugin's
 * event/ctx shape drifts from what the SDK expects; guarded by
 * `debug: true`. Path is logged (file paths are not secret); arg
 * *values* are not.
 */
function emitDebugProbe(
  event: unknown,
  ctx: unknown,
  memEntry: MemoryEntry | null,
  logger: GuardLogger | undefined,
): void {
  if (!logger?.info) return;

  const ev = (event ?? {}) as Record<string, unknown>;
  const c = (ctx ?? {}) as Record<string, unknown>;
  const argsCandidate =
    ev.params ?? c.params ?? ev.args ?? c.args ?? ev.arguments ?? c.arguments;
  const argsObj =
    argsCandidate && typeof argsCandidate === "object"
      ? (argsCandidate as Record<string, unknown>)
      : {};
  const argsKeys = Object.keys(argsObj).slice(0, 20);
  const pickedPath =
    (typeof argsObj.file_path === "string" && argsObj.file_path) ||
    (typeof argsObj.path === "string" && argsObj.path) ||
    (typeof argsObj.filename === "string" && argsObj.filename) ||
    null;
  const editsArr = Array.isArray(argsObj.edits) ? argsObj.edits : null;
  const firstEditKeys =
    editsArr && editsArr[0] && typeof editsArr[0] === "object"
      ? Object.keys(editsArr[0] as Record<string, unknown>).slice(0, 10)
      : null;
  const tool =
    (ev.toolName as string) ??
    (c.tool as { name?: string })?.name ??
    (c.toolName as string) ??
    (c.name as string) ??
    "(none)";

  logger.info("[atbash][probe] before_tool_call", {
    toolName: tool,
    eventKeys: Object.keys(ev).slice(0, 20),
    ctxKeys: Object.keys(c).slice(0, 20),
    argsKeys,
    pickedPath,
    firstEditKeys,
    editsCount: editsArr?.length,
    memoryWrite: !!memEntry,
    memoryPath: memEntry?.key,
  });
}

/**
 * Guarded memory-write flow. Returns `{ handled: false }` when the tool
 * call isn't a memory write, otherwise returns a `decision` the plugin
 * should return from its hook.
 *
 * Verdict handling:
 * - `red`  → not persisted to chain; decision blocks (unless `enforce: false`)
 * - `yellow` → persisted with LLM score; decision allows
 * - `green`  → persisted with LLM score; decision allows
 *
 * Chain commit is fire-and-forget: network errors are logged via
 * `logger.warn` but never surfaced to the caller. This mirrors the
 * existing plugin behaviour — chain is for audit/recovery, not gating.
 */
export async function guardMemoryWrite(
  input: GuardMemoryWriteInput,
): Promise<GuardMemoryWriteResult> {
  const {
    event,
    ctx,
    auth,
    endpoint,
    verifyPubKey,
    orgName,
    threshold,
    patterns,
    toolNames,
    enforce = true,
    debug = false,
    logger,
  } = input;

  const memEntry = classifyMemoryWrite(event, ctx, { patterns, toolNames });

  if (debug) emitDebugProbe(event, ctx, memEntry, logger);

  if (!memEntry) return { handled: false };

  // Scan — Layer 1 (regex) + Layer 2 (LLM) via scanMemory.
  let scanResult: MemoryScanResult;
  try {
    scanResult = await scanMemory(memEntry, auth, {
      endpoint,
      verifyPubKey,
      orgName,
      threshold,
    });
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    logger?.warn?.("[atbash] memory scan failed", {
      path: memEntry.key,
      reason,
    });
    // Fail closed when enforcing; fail open when observing.
    return {
      handled: true,
      decision: enforce
        ? { allow: false, block: true, reason }
        : { allow: true },
    };
  }

  // Red is final — never persisted. Poisoning payload should not become
  // a versioned snapshot even in observe mode.
  if (scanResult.verdict === "red") {
    logger?.warn?.("[atbash] memory BLOCK", {
      path: memEntry.key,
      reason: scanResult.reason,
    });
    return {
      handled: true,
      decision: enforce
        ? { allow: false, block: true, reason: scanResult.reason }
        : { allow: true },
      scanResult,
      committed: false,
    };
  }

  // Yellow / green — persist to chain fire-and-forget. LLM score wins
  // over any static verdict→score mapping (A2 in the plan).
  commitMemoryVersion(memEntry.value, auth, {
    score: scanResult.score,
    orgName,
    endpoint,
  }).catch((err: unknown) => {
    const reason = err instanceof Error ? err.message : String(err);
    logger?.warn?.("[atbash] memory commit to chain failed", {
      path: memEntry.key,
      reason,
    });
  });

  logger?.info?.(
    scanResult.verdict === "yellow" ? "[atbash] memory HOLD" : "[atbash] memory ALLOW",
    { path: memEntry.key, score: scanResult.score, reason: scanResult.reason },
  );

  return {
    handled: true,
    decision: { allow: true },
    scanResult,
    committed: true,
  };
}
